import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../src/app/store";

import {
  selectUploadInProgress,
  selectUploadError,
  selectPartyDropdownOptions,
  selectIsDocumentReplacing,
  selectSortedDocumentsWithPartyName,
  selectDocumentReplaceError,
} from "../logic/caseDocumentsSelector";

import {DOCUMENT_CONFIG} from "../config/DocumentConfig";
import {
  addCaseDocument,
  replaceCaseDocument,
} from "../../../../src/thunks/caseDocument";
import {
  DocumentType,
  DocumentEntityType,
  DocumentFileType,
  CaseDocument,
  DocumentScope,
} from "../../../../src/api/caseDocuments";
import {
  ChevronRight,
  FileText,
  Info,
  Loader2,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import SelectDropdownV2 from "../../../../src/components/ui/selectdropdown/v2";
import DocumentTooltip from "../components/DocumentTooltip";
import { cn } from "../../../../src/lib/utils";
import InlineError from "./InlineError";
import { checkDuplicate, DuplicateReason } from "../logic/checkDuplicacy";
import { showSuccessToast } from "../../../../src/lib/toast";
import { LEAType, LEATypeLabelMap } from "../../../../src/api/events";
import { getAccountProductInfo } from "../../creditAccounts/logic/slice";
import { isCreditAreaOfFraud } from "../../caseinfo/logic/Slice";

interface DuplicateInfo {
  duplicate: boolean;
  existingDoc: CaseDocument | null;
  reason: DuplicateReason| null;
  message: string | null;
}

const MAX_FILE_SIZE = 25 * 1024 * 1024;
const MAX_FILE_NAME_LENGTH = 40;

interface UploadDocumentModalProps {
  onClose: () => void;

  initialDocumentType?: DocumentType;

  initialEntityType?: DocumentEntityType;
  initialEntityId?: string;

  lockEntity?: boolean;
  lockDocumentType?: boolean;
  allowedDocumentTypes?: ReadonlySet<DocumentType> | readonly DocumentType[];
}

enum SubmitMode {
  Upload = "upload",
  Replace = "replace",
}

type DocumentTypeOption = {
  name: string;
  value: DocumentType;
};

export default function UploadDocumentModal({
  onClose,
  initialDocumentType,
  initialEntityType,
  initialEntityId,
  lockEntity,
  lockDocumentType,
  allowedDocumentTypes,
}: UploadDocumentModalProps) {
  // File handling
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState("");
  const [documentType, setDocumentType] =  useState<DocumentTypeOption | null>(null);
  const [notes, setNotes] = useState("");
  const [partyId, setPartyId] = useState("");
  const [loanId, setLoanId] = useState("");
  const [leaType, setLeaType] = useState<LEAType | "">("");
  const [meetingId, setMeetingId] = useState("");


  const [localProgress, setLocalProgress] = useState(0);

  const [duplicateInfo, setDuplicateInfo] = useState<DuplicateInfo>({
    duplicate: false,
    existingDoc: null,
    reason: null,
    message: null,
  });

  const [hasSubmitted, setHasSubmitted] = useState<
    SubmitMode.Upload | SubmitMode.Replace | null
  >(null);

  const progressInterval = useRef<any>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const replacingDocument = duplicateInfo.existingDoc;

  const isReplacing = useAppSelector(
    replacingDocument
      ? selectIsDocumentReplacing(replacingDocument.document_id)
      : () => false
  );

  const replaceError = useAppSelector(
    replacingDocument
      ? selectDocumentReplaceError(replacingDocument.document_id)
      : () => null
  );

  const { id: case_id } = useParams<{ id: string }>();

  const dispatch = useAppDispatch();

  const uploading = useAppSelector(selectUploadInProgress);
  const uploadError = useAppSelector(selectUploadError);

  const parties = useAppSelector(selectPartyDropdownOptions);

  const documents = useAppSelector(selectSortedDocumentsWithPartyName);

  const accountProductInfo = useAppSelector(getAccountProductInfo)
  const loanOptions = useMemo(
    () =>
      accountProductInfo.map((account) => ({
        name: account.ext_account_number,
        value: account.loan_reference_id,
      })),
    [accountProductInfo],
  );

  const isLoanLocked =lockEntity && initialEntityType === DocumentEntityType.LOAN_ACCOUNT;

  const isPartyLocked =lockEntity && initialEntityType === DocumentEntityType.PARTY_INVOLVED;

  const isLeaLocked = lockEntity && initialEntityType === DocumentEntityType.LEA;


  const isDisabled = uploading|| isReplacing;
  const hasParties = parties.length > 0;
  const isPartyDisabled = isDisabled || !hasParties || isPartyLocked;

  const isCreditCase = useAppSelector(isCreditAreaOfFraud);

  const documentTypeOptions: DocumentTypeOption[] = Object.entries(DOCUMENT_CONFIG)
    ?.filter(([type]) => {
      if (!allowedDocumentTypes) return true;
      const set =
        allowedDocumentTypes instanceof Set
          ? allowedDocumentTypes
          : new Set(allowedDocumentTypes);
      return set.has(type as DocumentType);
    })
    .filter(([, config]) => (!(config.scope === DocumentScope.LOAN_ACCOUNT && !isCreditCase)))
    .sort(([, a], [, b]) => a.order - b.order)
    .map(([type, config]) => ({
      name: config.dropdownLabel!,
      value: type as DocumentType,
    }));

  useEffect(() => {
    if (!lockEntity) {
      setPartyId("");
      setLoanId("");
      setLeaType("");
      setMeetingId("");
      return;
    }

    if (initialEntityType !== DocumentEntityType.PARTY_INVOLVED) {
      setPartyId("");
    }

    if (initialEntityType !== DocumentEntityType.LOAN_ACCOUNT) {
      setLoanId("");
    }

    if (initialEntityType !== DocumentEntityType.LEA) {
      setLeaType("");
    }

    if (initialEntityType !== DocumentEntityType.PRE_SCN_FIC_MEETING) {
      setMeetingId("");
    }

  }, [documentType?.value, lockEntity, initialEntityType]);


useEffect(() => {
  if (!initialEntityType || !initialEntityId) return;

    if (initialDocumentType) {
    setDocumentType({
      name: DOCUMENT_CONFIG[initialDocumentType].dropdownLabel!,
      value: initialDocumentType,
    });
  }

  if (initialEntityType === DocumentEntityType.PARTY_INVOLVED) {
    setPartyId(initialEntityId);
  }

  if (initialEntityType === DocumentEntityType.LOAN_ACCOUNT) {
    setLoanId(initialEntityId);
  }

  if (initialEntityType === DocumentEntityType.LEA) {
    setLeaType(initialEntityId as LEAType);
  }

  if (initialEntityType === DocumentEntityType.PRE_SCN_FIC_MEETING) {
    setMeetingId(initialEntityId);
  }

}, [initialEntityType, initialEntityId, initialDocumentType]);

  const validateFile = (fileObj: File) => {
    if (!fileObj) return;

    const fileName = fileObj.name;
    const lowerName = fileName.toLowerCase();

    if (!lowerName.endsWith(".pdf")) {
      setFileError("Only PDF files are allowed");
      return;
    }

    if (fileObj.size > MAX_FILE_SIZE) {
      setFileError("File size must be less than 25 MB");
      return;
    }

    if (fileName.length > MAX_FILE_NAME_LENGTH) {
      setFileError("File name must be under 40 characters");
      return;
    }

    const safeFileNameRegex = /^[a-zA-Z0-9_() -]+\.[a-zA-Z0-9]+$/;

    if (!safeFileNameRegex.test(fileName)) {
      setFileError(
        "File name must contain only letters, numbers, spaces, hyphens, underscores, parentheses, and a single extension."
      );
      return;
    }

    setFile(fileObj);
    setFileError("");
  };

  useEffect(() => {
    if (!file || !documentType) {
      setDuplicateInfo({
        duplicate: false,
        existingDoc: null,
        reason: null,
        message: null,
      });
      return;
    }

    if (hasSubmitted) return;

    const result = checkDuplicate(
      documents,
      documentType.value,
      partyId || null,
      file.name
    );

    setDuplicateInfo(result);
  }, [file, documentType, partyId, documents, hasSubmitted]);

  const startFakeProgress = () => {
    setLocalProgress(5);

    progressInterval.current = setInterval(() => {
      setLocalProgress((p) => Math.min(p + Math.random() * 10 + 5, 90));
    }, 450);
  };

  const stopProgress = () => {
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
      progressInterval.current = null;
    }
  };

  const config = documentType ? DOCUMENT_CONFIG[documentType.value] : null;

  const resolvedEntityType = initialEntityType && lockEntity ? initialEntityType :
    config?.scope === DocumentScope.PARTY
    ? DocumentEntityType.PARTY_INVOLVED
    : config?.scope === DocumentScope.LOAN_ACCOUNT
    ? DocumentEntityType.LOAN_ACCOUNT
    : documentType?.value === DocumentType.LEA_REPORT
    ? DocumentEntityType.LEA
    : config?.scope === DocumentScope.PRE_SCN_FIC_MEETING
    ? DocumentEntityType.PRE_SCN_FIC_MEETING
    : DocumentEntityType.CASE;
  
  const resolvedEntityId =
  resolvedEntityType === DocumentEntityType.PARTY_INVOLVED
    ? partyId
    : resolvedEntityType === DocumentEntityType.LOAN_ACCOUNT
    ? loanId
    : resolvedEntityType === DocumentEntityType.LEA
    ? leaType
    : resolvedEntityType === DocumentEntityType.PRE_SCN_FIC_MEETING
    ? meetingId
    : case_id;  

  const handleUpload = async () => {
    if (isDisabled) return;
    if (fileError) return;
    if (!file || !documentType) return;
    if (duplicateInfo.duplicate) return;
    if(!resolvedEntityId) return;

    setHasSubmitted(SubmitMode.Upload );
    startFakeProgress();

    try {
      if (!case_id) return;

      await dispatch(
        addCaseDocument({
          case_id,
          payload: {
            document_type: documentType.value,
            entity_type:resolvedEntityType,
            entity_id:resolvedEntityId,
            file_name: file.name,
            file_type: DocumentFileType.PDF,
            notes: notes || undefined,
          },
          file,
        })
      ).unwrap();    

      showSuccessToast("Document has been uploaded successfully");
    
    onClose();  
    } catch {
      stopProgress();
    }
  };

  const handleReplace = async () => {
    if (isDisabled) return;
    if (fileError) return;
    if (!case_id || !file || !documentType) return;
    if (!replacingDocument) return;
    if(!resolvedEntityId) return;

    setHasSubmitted(SubmitMode.Replace);
    startFakeProgress();

    try {
      if (!case_id) return;

      await dispatch(
        replaceCaseDocument({
          case_id,
          document_id: replacingDocument.document_id,
          payload: {
            document_type: documentType.value,
            entity_type:resolvedEntityType,
            entity_id:resolvedEntityId,             
            file_name: file.name,
            file_type: DocumentFileType.PDF,
            notes: notes || undefined,
          },
          file,
        })
      ).unwrap();

      showSuccessToast("Document has been replaced successfully");

      onClose();
    } catch {
      stopProgress();
    }
  };

  const handleRemoveFile = () => {
    if (isDisabled) return;

    setFile(null);
    setFileError("");
    setHasSubmitted(null);
    setLocalProgress(0);
    stopProgress();

    setDuplicateInfo({
      duplicate: false,
      existingDoc: null,
      reason: null,
      message: null,
    });
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();

    const droppedFile = e.dataTransfer.files?.[0];
    if (!droppedFile) return;

    if (droppedFile.type !== "application/pdf") {
      setFileError("Only PDF files are allowed.");
      return;
    }

    validateFile(droppedFile);
  };

  const requiresParty = config?.scope === DocumentScope.PARTY;
  const requiresLoan = config?.scope === DocumentScope.LOAN_ACCOUNT;
  const isLeaDocument = documentType?.value === DocumentType.LEA_REPORT;

  return (
    <div className="fixed inset-0 bg-[rgba(10,10,10,0.20)] flex items-center justify-center z-[9999]">
      <div className="bg-[#FFF] w-[425px] max-w-[425px] border border-[#E5E5E5] rounded-[10px] shadow-lg p-6 flex flex-col gap-4 relative">
        {/* ---------------- HEADER ---------------- */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[#0A0A0A]">
            Upload Documents
          </h2>
          <button
            onClick={onClose}
            disabled={isDisabled}
            className={cn("absolute right-4 top-4 text-gray-700 rounded-xs",
               "hover:text-gray-900 hover:bg-gray-100",
              "disabled:text-gray-300 disabled:hover:text-gray-300",
              "disabled:hover:bg-transparent disabled:cursor-not-allowed"
            )}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ---------------- UPLOAD ZONE (NO FILE) ---------------- */}
        {!file ? (
          <div
            className="flex h-[100px] px-6 py-6 flex-col justify-center rounded-[8px] items-center gap-2 border border-dashed border-[#3551F3] bg-[#EBEEFE]"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
          >
            <div className="flex py-1 px-4 items-center gap-2 rounded-md bg-[#EBEEFE]">
              <Upload className="w-4 h-4 text-[#3551F3]" />
              <p className="text-[#3551F3] text-sm font-medium">
                Upload PDF or Image
              </p>
            </div>

            <p className="text-[#737373] text-xs text-center leading-4">
              Select a file to upload or drag and drop
              <br />
              Accepted: .pdf (max 25 MB)
            </p>

            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={(e) => validateFile(e.target.files?.[0] as File)}
            />
          </div>
        ) : (
          <div className="w-full h-[100px] rounded-[8px] bg-[#EBEEFE] flex items-center">
            <div className="flex flex-col w-full px-6 py-4 gap-3">
              <div className="flex items-center justify-center gap-2">
                <div className="flex items-center justify-center w-8 h-8 p-2 rounded-md bg-[#F8FAFC]">
                  <FileText className="h-4 w-4 text-slate-600" />
                </div>

                <div className="flex flex-col gap-1 flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-black truncate">
                      {file.name}
                    </p>

                    <div className="flex items-center flex-shrink-0 gap-1">
                      <span className="text-sm font-normal leading-normal font-sans text-[#9CA3AF]">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </span>

                      {!uploading && !isReplacing && (
                        <button
                          onClick={handleRemoveFile}
                          className="p-1 rounded-full hover:bg-gray-200"
                        >
                          <Trash2 className="h-4 w-4 text-[#737373]" />
                        </button>
                      )}
                    </div>
                  </div>

                  {hasSubmitted && isDisabled && (
                    <div className="w-full h-2 bg-white/80 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#3B82F6] transition-all duration-300"
                        style={{ width: `${localProgress}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {duplicateInfo.duplicate && duplicateInfo.reason === DuplicateReason.SameDocumentName && (
         <InlineError message={duplicateInfo.message} />
        )}

        {fileError && ( <InlineError message={fileError} className="items-center"/> )}

        {/* ---------------- DOCUMENT TYPE ---------------- */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-[#0A0A0A]">
            Document Type
          </label>

          <SelectDropdownV2
            name="Document Type"
            placeholder="Select file type"
            enableSearch={false}
            disabled={isDisabled || lockDocumentType}
            options={documentTypeOptions}
            selectedOption={documentType ?? undefined
            }
            onSelectionChange={(opt) => setDocumentType(opt)}
            containerClassName={cn(
              "h-9 w-full border border-[#E5E5E5] rounded-md bg-white shadow-xs px-3 py-1 flex items-center",
              (isDisabled || lockDocumentType) &&
                "bg-gray-50 border-gray-200 shadow-none cursor-not-allowed"
            )}
            labelClassName={cn(
              "flex-1 text-sm font-normal leading-5 text-[#0A0A0A] font-sans w-full",
              (isDisabled || lockDocumentType) && "text-gray-400"
            )}
            focusClassName="outline outline-[2px] outline-indigo-200"
          />
        </div>

        {duplicateInfo.duplicate && duplicateInfo.reason === DuplicateReason.SameDocumentType && (
           <InlineError message={duplicateInfo.message} />
        )}
      
        {/* ---------------- LOAN ACCOUNT ---------------- */}
        {requiresLoan && (
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-[#0A0A0A]">
              Loan Account
            </label>

            <SelectDropdownV2
              name="Loan Account"
              placeholder="Select Loan Account"
              enableSearch={false}
              disabled={isDisabled || loanOptions.length === 0 || isLoanLocked}
              options={loanOptions}
              selectedOption={
                loanId
                  ? loanOptions.find((option) => option.value === loanId)
                  : undefined
              }
              onSelectionChange={(opt) => setLoanId(opt.value)}
              containerClassName={cn(
                "h-9 w-full border border-[#E5E5E5] rounded-md bg-white shadow-xs px-3 py-1 flex items-center",
                (isDisabled || isLoanLocked) &&
                  "bg-gray-50 border-gray-200 shadow-none cursor-not-allowed"
              )}
              labelClassName={cn(
                "flex-1 text-sm font-normal leading-5 text-[#0A0A0A] font-sans w-full",
                (isDisabled || isLoanLocked) && "text-gray-400"
              )}
              focusClassName="outline outline-[2px] outline-indigo-200"
            />
          </div>
        )}


         {/* ---------------- LEA Document ---------------- */}
        {isLeaDocument && (
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-[#0A0A0A]">
              Law Enforcement Agency
            </label>

            <SelectDropdownV2
              name="LEA Type"
              placeholder="Select LEA"
              enableSearch={false}
              disabled={isDisabled || isLeaLocked}
              options={Object.values(LEAType).map((value) => ({
              name: LEATypeLabelMap[value],
              value,
              }))}
              selectedOption={
               leaType ? 
               {
                 name: LEATypeLabelMap[leaType],
                 value: leaType,
               }
              : undefined
              }
              onSelectionChange={(opt) => setLeaType(opt.value)}
              containerClassName={cn(
                "h-9 w-full border border-[#E5E5E5] rounded-md bg-white shadow-xs px-3 py-1 flex items-center",
                (isDisabled || isLeaLocked) &&
                  "bg-gray-50 border-gray-200 shadow-none cursor-not-allowed"
              )}
              labelClassName={cn(
                "flex-1 text-sm font-normal leading-5 text-[#0A0A0A] font-sans w-full",
                (isDisabled || isLeaLocked) && "text-gray-400"
              )}
              focusClassName="outline outline-[2px] outline-indigo-200"
            />
          </div>
        )}
         
        {/* ---------------- PARTY NAME ---------------- */}
        {requiresParty && (
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-[#0A0A0A]">
              Party Name
            </label>

            <SelectDropdownV2
              name="Party"
              placeholder="Select Party"
              enableSearch={false}
              disabled={isPartyDisabled}
              options={parties
                .filter((p) => p.party_id && p.party_id !== "")
                .map((p) => ({
                  name: p.party_name,
                  value: p.party_id,
                }))}
              selectedOption={
                hasParties
                  ? partyId
                    ? parties
                        .map((p) => ({
                          name: p.party_name,
                          value: p.party_id,
                        }))
                        .find((opt) => opt.value === partyId)
                    : undefined
                  : {
                      name: "No Parties available",
                      value: "__no_party__",
                    }
              }
              onSelectionChange={(opt) => setPartyId(opt.value)}
              containerClassName={cn(
                "h-9 w-full border border-[#E5E5E5] rounded-md bg-white shadow-xs py-1 px-3 flex items-center",
                isPartyDisabled &&
                  "bg-gray-50 border-gray-200 shadow-none cursor-not-allowed"
              )}
              labelClassName={cn(
                "flex-1 text-sm font-normal leading-5 text-[#0A0A0A] font-sans w-full",
                isPartyDisabled && "text-gray-400"
              )}
              focusClassName="outline outline-[2px] outline-indigo-200"
            />
          </div>
        )}

        {duplicateInfo.duplicate &&
          duplicateInfo.reason === DuplicateReason.SameDocumentTypeSameParty && (
             <InlineError message={duplicateInfo.message} />
          )}

        {/* ---------------- NOTES ---------------- */}
        {config?.requiresNote && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-[#0A0A0A]">Note</label>
              <DocumentTooltip content={"Note should be under 200 characters"}>
                <Info className="h-3.5 w-3.5 text-[#737373]" />
              </DocumentTooltip>
            </div>

            <textarea
              maxLength={200}
              value={notes}
              disabled={isDisabled}
              onChange={(e) => setNotes(e.target.value)}
              className={cn(
                "h-[100px] w-full border border-[#E5E5E5] rounded-md px-3 py-1 shadow-sm text-sm font-normal text-[#0A0A0A] leading-5 font-sans transition-colors",
                isDisabled &&
                  "bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed"
              )}
              placeholder="Add a note..."
            />
          </div>
        )}

        {/* ---------------- FOOTER BUTTON ---------------- */}
        <div>
          {!duplicateInfo.duplicate ? (
            <button
              disabled={
                !file || !documentType || (uploading && hasSubmitted === SubmitMode.Upload) || (requiresParty && !partyId) || (requiresLoan && !loanId) || (isLeaDocument && !leaType)
              }
              onClick={handleUpload}
              className="flex h-9 px-4 py-2 justify-center items-center gap-2 self-stretch rounded-md border border-indigo-500 shadow-sm w-full disabled:opacity-50"
            >
              {uploading && hasSubmitted === SubmitMode.Upload ? (
                <>
                  <Loader2 className="h-4 w-4 text-[#3551F3] animate-spin" />
                  <span className="text-[#3551F3] font-medium text-sm leading-5">
                    Uploading…
                  </span>
                </>
              ) : (
                <>
                  <span className="text-[#3551F3] font-medium text-sm leading-5">
                    Save & Proceed
                  </span>
                  <ChevronRight className="h-4 w-4 text-[#3551F3]" />
                </>
              )}
            </button>
          ) : (
            <div className="flex justify-end gap-3">
              <button
                onClick={handleReplace}
                disabled={
                  !file ||
                  !documentType ||
                 (isReplacing && hasSubmitted === SubmitMode.Replace) ||
                  (requiresParty && !partyId) ||
                  (requiresLoan && !loanId) ||
                  (isLeaDocument && !leaType)
                }
                className="flex h-9 px-4 py-2 justify-center items-center gap-2 self-stretch rounded-md border border-indigo-500 shadow-sm w-full disabled:opacity-50"
              >
                {isReplacing && hasSubmitted === SubmitMode.Replace ? (
                  <>
                    <Loader2 className="h-4 w-4 text-[#3551F3] animate-spin" />
                    <span className="text-[#3551F3] font-medium text-sm leading-5">
                      Replacing…
                    </span>
                  </>
                ) : (
                  <>
                    <span className="text-[#3551F3] font-medium text-sm leading-5">
                      Replace
                    </span>
                    <ChevronRight className="h-4 w-4 text-[#3551F3]" />
                  </>
                )}
              </button>
            </div>
          )}

          {uploadError && hasSubmitted === SubmitMode.Upload  && (
           <InlineError message={`${uploadError}. Please retry!`} className="mt-2" />
          )}

          {replaceError && hasSubmitted === SubmitMode.Replace && (
            <InlineError message={`${replaceError}. Please retry!`} className="mt-2"/>
          )}
        </div>
      </div>
    </div>
  );
}